import { QueryKey } from "@/api";
import { Api } from "@/api/Api";
import { portfolioErrorMessage } from "@/portfolio/portfolioError";
import {
  type Portfolio,
  type PortfolioType,
  portfolioFromApi,
  portfolioPreviewFromApi,
  portfolioSummaryFromApi,
} from "@/portfolio/types";
import type { InitOverrideFunction } from "@mm/metx-workbench.meteomatics.com";
import { type QueryFunctionContext, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";

// Login/logout navigate away from the app, so these caches live for one authenticated session.
export const portfolioListKey = () => [QueryKey.PORTFOLIOS];
export const portfolioDetailKey = (id: string) => [QueryKey.PORTFOLIO, id];

export function usePortfolios(enabled = true) {
  return useQuery({
    queryKey: portfolioListKey(),
    queryFn: async ({ signal }) => (await Api.portfolio.v2ListPortfolios({ signal })).map(portfolioSummaryFromApi),
    enabled,
  });
}

export function portfolioDetailQueryOptions(id: string | undefined) {
  return {
    queryKey: portfolioDetailKey(id ?? ""),
    queryFn: async ({ signal }: QueryFunctionContext) => {
      if (!id) {
        throw new Error("Portfolio ID is required");
      }
      return portfolioFromApi(await Api.portfolio.v2ReadPortfolio({ portfolioId: id }, { signal }));
    },
    enabled: !!id,
  };
}

export function usePortfolio(id: string | undefined) {
  return useQuery(portfolioDetailQueryOptions(id));
}

export type PortfolioWrite =
  | { operation: "create"; file: File; portfolioType: PortfolioType; name: string; creationId: string }
  | { operation: "rename"; portfolioId: string; name: string }
  | { operation: "replace"; portfolioId: string; file: File }
  | { operation: "delete"; portfolioId: string };

export function usePortfolioApi() {
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  return useMemo(() => {
    async function request<T>(send: (options: InitOverrideFunction) => Promise<T>, signal?: AbortSignal) {
      function assertActive() {
        if (!mounted.current) {
          throw new Error("Portfolio manager closed");
        }
      }
      assertActive();
      try {
        const csrf = await Api.security.getCsrfTokenApiV2SecurityCsrfTokenGet({ credentials: "include" });
        assertActive();
        return await send(({ init }) => {
          assertActive();
          return Promise.resolve({
            credentials: "include",
            headers: { ...init.headers, "X-CSRF-Token": csrf.csrf_token },
            ...(signal ? { signal } : {}),
          });
        });
      } catch (error) {
        throw new Error(await portfolioErrorMessage(error));
      }
    }
    return {
      preview: (file: File, portfolioType: PortfolioType, signal?: AbortSignal) =>
        request(
          async (options) =>
            portfolioPreviewFromApi(await Api.portfolio.v2PreviewPortfolio({ file, portfolioType }, options)),
          signal,
        ),
      write: (command: PortfolioWrite) =>
        request(async (options) => {
          switch (command.operation) {
            case "create":
              return portfolioFromApi(
                await Api.portfolio.v2CreatePortfolio(
                  {
                    file: command.file,
                    portfolioType: command.portfolioType,
                    name: command.name,
                    creationId: command.creationId,
                  },
                  options,
                ),
              );
            case "rename":
              return portfolioFromApi(
                await Api.portfolio.v2RenamePortfolio(
                  { portfolioId: command.portfolioId, name: command.name },
                  options,
                ),
              );
            case "replace":
              return portfolioFromApi(
                await Api.portfolio.v2ReplacePortfolio(
                  { portfolioId: command.portfolioId, file: command.file },
                  options,
                ),
              );
            case "delete":
              return portfolioFromApi(
                await Api.portfolio.v2DeletePortfolio({ portfolioId: command.portfolioId }, options),
              );
          }
        }),
    };
  }, []);
}

export type PortfolioApi = ReturnType<typeof usePortfolioApi>;

export function usePortfolioWrite(api: PortfolioApi) {
  const client = useQueryClient();
  const locked = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const mutation = useMutation<Portfolio, Error, PortfolioWrite>({
    mutationFn: (command) => api.write(command),
    retry: false,
    onSuccess: async (portfolio, command) => {
      if (!mounted.current) {
        return;
      }
      const key = portfolioDetailKey(command.operation === "create" ? portfolio.id : command.portfolioId);
      await client.cancelQueries({ queryKey: key, exact: true });
      if (!mounted.current) {
        return;
      }
      if (command.operation === "delete") {
        // Keep a tombstone while mounted consumers are observing this detail.
        // Removing an active React Query entry leaves observers holding their
        // previous result and the map would continue rendering deleted assets.
        client.setQueryData<Portfolio | null>(key, null);
        // Clear active observers' previous results and recheck availability.
        // Inactive entries retain the tombstone until their next read.
        void client.resetQueries({ queryKey: key, exact: true, type: "active" });
      } else {
        client.setQueryData(key, portfolio);
      }
      void client.invalidateQueries({ queryKey: portfolioListKey(), exact: true });
    },
    onSettled: () => {
      locked.current = false;
    },
  });
  return {
    busy: mutation.isPending,
    error: mutation.error?.message,
    operation: mutation.variables?.operation,
    reset: () => {
      if (!locked.current) {
        mutation.reset();
      }
    },
    isLocked: () => locked.current || !mounted.current,
    submit: (command: PortfolioWrite, callbacks?: { onSuccess: (portfolio: Portfolio) => void }) => {
      if (locked.current || !mounted.current) {
        return;
      }
      locked.current = true;
      mutation.mutate(command, {
        onSuccess: (portfolio) => {
          if (mounted.current) {
            callbacks?.onSuccess(portfolio);
          }
        },
      });
    },
  };
}

export type PortfolioWriter = ReturnType<typeof usePortfolioWrite>;
